import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ErrorBanner, Icon, Popover } from "@/components/ui";
import { workspacesApi } from "@/services/backend";
import { useAuth } from "@/context/AuthContext";
import { rememberTenantForUser, tenantMemoryKey } from "@/utils/tenantMemory";

export function WorkspaceSwitcher({ compact = false }: { compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(
    null,
  );
  const [switchError, setSwitchError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, currentTenantId, setCurrentTenantId } = useAuth();
  const q = useQuery({
    queryKey: ["auth", "workspaces"],
    queryFn: () => workspacesApi.mine(),
    enabled: !!user,
  });
  const workspaces = q.data ?? [];
  const current = workspaces.find((w) => w.tenantId === currentTenantId);
  const label = current?.name ?? "Selecciona tenant";
  const meta = current
    ? `${current.plan} · ${current.role}`
    : "Tenant requerido";
  const initial = current?.name?.[0] ?? "?";
  const userKey = tenantMemoryKey(user?.id, user?.email);

  const switchTenant = async (tenantId: string) => {
    if (switchingTenantId) return;
    setSwitchError(null);
    setSwitchingTenantId(tenantId);
    try {
      const tokens = await workspacesApi.switch(tenantId);
      const nextTenantId = tokens.tenantId ?? tenantId;
      rememberTenantForUser(userKey, nextTenantId);
      setCurrentTenantId(nextTenantId);
      queryClient.clear();
      setOpen(false);
      setSwitchingTenantId(null);
      navigate("/dashboard", { replace: true });
    } catch {
      setSwitchError("No se pudo activar el tenant. Intenta nuevamente.");
      setSwitchingTenantId(null);
    }
  };

  return (
    <div className={`${compact ? "px-2 pb-2" : "px-2.5 pb-2.5"} relative`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center rounded-lg bg-surface border border-border text-left hover:border-border-strong transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 ${
          compact
            ? "mx-auto h-10 w-10 justify-center p-0"
            : "w-full gap-2.5 p-2.5"
        }`}
        aria-label={
          compact ? `Cambiar workspace: ${label}` : "Cambiar workspace"
        }
        title={compact ? label : undefined}
      >
        <div className="w-7 h-7 rounded-md bg-primary-soft text-primary-ink grid place-items-center font-serif font-bold text-xs shrink-0">
          {initial.toUpperCase()}
        </div>
        {!compact && (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate" title={label}>
                {label}
              </div>
              <div className="text-[10.5px] text-muted truncate" title={meta}>
                {meta}
              </div>
            </div>
            <Icon name="chevronD" size={14} className="text-muted shrink-0" />
          </>
        )}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        className={
          compact
            ? "left-full top-0 ml-2 mt-0 w-72 p-1.5"
            : "left-2.5 right-2.5 mt-1 p-1.5"
        }
      >
        {switchError && (
          <ErrorBanner
            title="Cambio de tenant fallido"
            message={switchError}
            className="mb-1"
          />
        )}
        {workspaces.map((w) => (
          <button
            key={w.id}
            onClick={() => {
              void switchTenant(w.tenantId);
            }}
            disabled={Boolean(switchingTenantId)}
            className="w-full flex items-center gap-2.5 p-2 rounded-md hover:bg-bg-2 text-left disabled:cursor-wait disabled:opacity-70"
          >
            <div className="w-6 h-6 rounded bg-bg-2 grid place-items-center font-serif font-bold text-[11px]">
              {w.name[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{w.name}</div>
              <div className="text-[10px] text-muted">
                {w.plan} · {w.role}
              </div>
            </div>
            {switchingTenantId === w.tenantId ? (
              <Icon
                name="spinner"
                size={14}
                className="animate-spin text-primary"
              />
            ) : (
              w.tenantId === currentTenantId && (
                <Icon name="check" size={14} className="text-primary" />
              )
            )}
          </button>
        ))}
      </Popover>
    </div>
  );
}
